import { supabase } from '@/utils/supabase';
import { ensureHttps } from '@/utils/urlUtils';
import type { error } from 'console';
import { v4 as uuidv4 } from 'uuid';

// Types
export type ChatType = 'direct' | 'group';

export interface User {
  userId: string;
  firstName: string;
  lastName: string;
  imageUrl: string;
  username: string;
  online?: boolean;
  lastSeen?: string;
}

export interface Chat {
  id: string;
  name?: string;
  type: ChatType;
  participants: string[];
  lastMessageTime?: string;
  lastMessage?: string;
  imageUrl?: string;
  createdBy?: string;
  createdAt?: string;
}

export interface Message {
  id: string;
  chatId: string;
  senderId: string;
  text: string;
  imageUrl?: string;
  timestamp: string;
  linkPreview?: LinkPreview;
  reactions?: { [userId: string]: string };
}

export interface LinkPreview {
  title: string;
  description: string;
  image?: string;
  url: string;
}

interface ChatParticipant {
  id: string;
  chatId: string;
  userId: string;
  lastReadAt?: string;
  joinedAt: string;
}

// Messaging Service
export const messagingService = {
  // User presence
  updateUserPresence: async (userId: string, isOnline: boolean) => {
    if (!userId) return;

    try {
      await supabase
        .from('profiles')
        .update({
          online: isOnline,
          last_seen: new Date().toISOString(),
        })
        .eq('userId', userId);

    } catch (error) {
      console.error('Error updating user presence:', error);
    }
  },

  // Fetch user profile
  fetchUserProfile: async (userId: string): Promise<User | null> => {
    if (!userId) return null;

    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, username, first_name, last_name, imageUrl, online, last_seen')
        .eq('id', userId)
        .single();

      if (error) throw error;

      if (data) {
        return {
          userId: data.id,
          firstName: data.first_name || '',
          lastName: data.last_name || '',
          imageUrl: data.imageUrl || '/assets/images/fallback.jpg',
          username: data.username || '',
          online: data.online || false,
          lastSeen: data.last_seen,
        };
      }
      return null;
    } catch (error) {
      console.error('Error fetching user profile:', error);
      return null;
    }
  },

  // Fetch multiple user profiles
  fetchUserProfiles: async (userIds: string[]): Promise<{ [key: string]: User }> => {
    if (!userIds.length) return {};

    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('userId, username, first_name, last_name, imageUrl, online, last_seen')
        .in('userId', userIds);

      if (error) throw error;

      const userMap: { [key: string]: User } = {};

      if (data) {
        data.forEach(user => {
          userMap[user.userId] = {
            userId: user.userId,
            firstName: user.first_name || '',
            lastName: user.last_name || '',
            imageUrl: user.imageUrl || '/assets/images/fallback.jpg',
            username: user.username || '',
            online: user.online || false,
            lastSeen: user.last_seen,
          };
        });
      }

      return userMap;
    } catch (error) {
      console.error('Error fetching user profiles:', error);
      return {};
    }
  },

  // Chat methods
  fetchChat: async (chatId: string): Promise<Chat | null> => {
    if (!chatId) return null;

    try {
      // Fetch the chat
      const { data: chat, error: chatError } = await supabase
        .from('chats')
        .select('*')
        .eq('id', chatId)
        .single();

      if (chatError) throw chatError;

      if (!chat) return null;

      // Get participants for this chat with their profile information
      const { data: participantsWithProfiles, error: participantsError } = await supabase
        .from('chat_participants')
        .select(`
          user_id,
          profiles!inner(userId, first_name, last_name, imageUrl)
        `)
        .eq('chat_id', chatId);

      if (participantsError) {
        console.error('Error fetching participants with profiles:', participantsError);
      }

      // Extract participant IDs and format participant names
      const participants = participantsWithProfiles?.map(p => p.user_id) || [];

      // For direct chats, use the other participant's name if no chat name is set
      let chatName = chat.name;
      if (!chatName && chat.type === 'direct' && participantsWithProfiles) {
        // For the current user, we need to find the other participant
        const otherParticipants = participantsWithProfiles.filter(p =>
          p.profiles && p.user_id !== chat.created_by
        );

        if (otherParticipants.length > 0) {
          const otherParticipant = otherParticipants[0];
          chatName = `${otherParticipant.profiles.first_name} ${otherParticipant.profiles.last_name}`.trim();
        }
      }

      return {
        id: chat.id,
        name: chatName || '',
        type: chat.type,
        participants: participants,
        lastMessageTime: chat.last_message_time,
        lastMessage: chat.last_message,
        imageUrl: chat.image_url,
        createdBy: chat.created_by,
        createdAt: chat.created_at
      };
    } catch (error) {
      console.error('Error fetching chat:', error);
      return null;
    }
  },

  fetchChats: async (userId: string): Promise<Chat[]> => {
    try {
      // First get all chat IDs where the user is a participant
      const { data: participantData, error: participantError } = await supabase
        .from('chat_participants')
        .select('chat_id')
        .eq('user_id', userId);

      if (participantError) throw participantError;

      if (!participantData || participantData.length === 0) return [];

      const chatIds = participantData.map(p => p.chat_id);

      // Then fetch the actual chats
      const { data: chatsData, error: chatsError } = await supabase
        .from('chats')
        .select('*')
        .in('id', chatIds)
        .order('last_message_time', { ascending: false });

      if (chatsError) throw chatsError;

      if (!chatsData) return [];

      // For each chat, get its participants with profile information
      const chatsWithParticipants = await Promise.all(
        chatsData.map(async (chat) => {
          // Get participants with their profile information
          const { data: participantsWithProfiles } = await supabase
            .from('chat_participants')
            .select(`
              user_id,
              profiles!inner(userId, first_name, last_name, imageUrl)
            `)
            .eq('chat_id', chat.id);

          // Extract participant IDs
          const participants = participantsWithProfiles?.map(p => p.user_id) || [];

          // For direct chats, use the other participant's name if no chat name is set
          let chatName = chat.name;
          if (!chatName && chat.type === 'direct' && participantsWithProfiles) {
            // Find the other participant (not the current user)
            const otherParticipants = participantsWithProfiles.filter(p =>
              p.profiles && p.user_id !== userId
            );

            if (otherParticipants.length > 0) {
              const otherParticipant = otherParticipants[0];
              chatName = `${otherParticipant.profiles.first_name} ${otherParticipant.profiles.last_name}`.trim();
            }
          }

          // Get the image URL for direct chats (use the other participant's image)
          let imageUrl = chat.image_url;
          if (!imageUrl && chat.type === 'direct' && participantsWithProfiles) {
            const otherParticipant = participantsWithProfiles.find(p => p.user_id !== userId);
            if (otherParticipant && otherParticipant.profiles) {
              imageUrl = otherParticipant.profiles.imageUrl;
            }
          }

          return {
            id: chat.id,
            name: chatName || '',
            type: chat.type,
            participants: participants,
            lastMessageTime: chat.last_message_time,
            lastMessage: chat.last_message,
            imageUrl: imageUrl,
            createdBy: chat.created_by,
            createdAt: chat.created_at
          };
        })
      );

      return chatsWithParticipants;
    } catch (error) {
      console.error('Error fetching chats:', error);
      return [];
    }
  },

  createChat: async (
    userId: string,
    participants: string[],
    type: ChatType = 'direct',
    name?: string,
    imageUrl?: string
  ): Promise<string | null> => {
    if (!userId || participants.length === 0) return null;

    try {
      // Get the userId string from profiles based on the numeric ID
      const { data: userData, error: userError } = await supabase
        .from('profiles')
        .select('userId')
        .eq('userId', userId)
        .single();

      if (userError) {
        console.error('Error fetching user data:', userError);
        console.log('cant find user with id', userId);
        return null;
      }

      if (!userData || !userData.userId) {
        console.error('User not found or userId missing');
        return null;
      }

      const userIdString = userData.userId;

      // For direct chats, check if a chat already exists between these two users
      if (type === 'direct' && participants.length === 1) {
        const otherUserId = participants[0];

        // Find direct chats where both users are participants
        // First, get all chats where the current user is a participant
        const { data: userChats } = await supabase
          .from('chat_participants')
          .select('chat_id')
          .eq('user_id', userIdString);

        if (userChats && userChats.length > 0) {
          // Get the chat IDs where the current user is a participant
          const chatIds = userChats.map(chat => chat.chat_id);

          // Find chats where the other user is also a participant
          const { data: sharedChats } = await supabase
            .from('chat_participants')
            .select('chat_id')
            .eq('user_id', otherUserId)
            .in('chat_id', chatIds);

          if (sharedChats && sharedChats.length > 0) {
            // For each potential shared chat, check if it's a direct chat with exactly 2 participants
            for (const chat of sharedChats) {
              const { data: chatData } = await supabase
                .from('chats')
                .select('type')
                .eq('id', chat.chat_id)
                .eq('type', 'direct')
                .single();

              if (chatData) {
                // Check if this chat has exactly 2 participants
                const { count } = await supabase
                  .from('chat_participants')
                  .select('*', { count: 'exact', head: true })
                  .eq('chat_id', chat.chat_id);

                if (count === 2) {
                  // Found an existing direct chat between these two users
                  return chat.chat_id;
                }
              }
            }
          }
        }
      }

      // Create a new chat
      const chatId = uuidv4();

      // Insert the chat
      const { error: chatError } = await supabase
        .from('chats')
        .insert({
          id: chatId,
          type,
          name,
          image_url: imageUrl,
          created_by: userIdString,
          created_at: new Date().toISOString()
        });

      if (chatError) throw chatError;

      // Add all participants including the creator
      const allParticipants = [userIdString, ...participants];
      const participantInserts = allParticipants.map(participantId => ({
        chat_id: chatId,
        user_id: participantId,
        joined_at: new Date().toISOString()
      }));

      const { error: participantError } = await supabase
        .from('chat_participants')
        .insert(participantInserts);

      if (participantError) throw participantError;

      return chatId;
    } catch (error) {
      console.error('Error creating chat:', error);
      return null;
    }
  },

  deleteChat: async (chatId: string): Promise<boolean> => {
    if (!chatId) return false;

    try {
      // Delete the chat (cascade will handle participants and messages)
      const { error } = await supabase
        .from('chats')
        .delete()
        .eq('id', chatId);

      if (error) throw error;

      return true;
    } catch (error) {
      console.error('Error deleting chat:', error);
      return false;
    }
  },

  markChatAsRead: async (chatId: string, userId: string): Promise<boolean> => {
    if (!chatId || !userId) return false;

    try {
      const { error } = await supabase
        .from('chat_participants')
        .update({ last_read_at: new Date().toISOString() })
        .eq('chat_id', chatId)
        .eq('user_id', userId);

      if (error) throw error;

      return true;
    } catch (error) {
      console.error('Error marking chat as read:', error);
      return false;
    }
  },

  // Message methods
  fetchMessages: async (chatId: string): Promise<Message[]> => {
    if (!chatId) return [];

    try {
      // Fetch messages
      const { data, error } = await supabase
        .from('messages')
        .select('*')
        .eq('chat_id', chatId)
        .order('created_at', { ascending: true });

      if (error) throw error;

      if (!data) return [];

      // For each message, get its reactions
      const messages: Message[] = await Promise.all(
        data.map(async message => {
          const { data: reactionsData } = await supabase
            .from('message_reactions')
            .select('user_id, emoji')
            .eq('message_id', message.id);

          const reactions = reactionsData?.reduce((acc, reaction) => {
            acc[reaction.user_id] = reaction.emoji;
            return acc;
          }, {} as { [userId: string]: string }) || {};

          return {
            id: message.id,
            chatId: message.chat_id,
            senderId: message.sender_id,
            text: message.text,
            imageUrl: message.image_url,
            timestamp: message.created_at,
            linkPreview: message.link_preview,
            reactions
          };
        })
      );

      return messages;
    } catch (error) {
      console.error('Error fetching messages:', error);
      return [];
    }
  },

  sendMessage: async (
    chatId: string,
    senderId: string,
    text: string,
    linkPreview?: LinkPreview,
    imageUrl?: string
  ): Promise<string | null> => {
    if (!chatId || !senderId) return null;

    try {
      // Create message
      const messageId = uuidv4();
      const timestamp = new Date().toISOString();

      const { error: messageError } = await supabase
        .from('messages')
        .insert({
          id: messageId,
          chat_id: chatId,
          sender_id: senderId,
          text,
          image_url: imageUrl,
          link_preview: linkPreview,
          created_at: timestamp
        });

      if (messageError) throw messageError;

      // Update chat's last message
      const { error: chatError } = await supabase
        .from('chats')
        .update({
          last_message: imageUrl ? '[Image]' : text,
          last_message_time: timestamp
        })
        .eq('id', chatId);

      if (chatError) throw chatError;

      return messageId;
    } catch (error) {
      console.error('Error sending message:', error);
      return null;
    }
  },

  deleteMessage: async (messageId: string, chatId: string): Promise<boolean> => {
    if (!messageId || !chatId) return false;

    try {
      // Delete the message
      const { error } = await supabase
        .from('messages')
        .delete()
        .eq('id', messageId);

      if (error) throw error;

      // Find the new last message and update the chat
      const { data: lastMessage } = await supabase
        .from('messages')
        .select('*')
        .eq('chat_id', chatId)
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      if (lastMessage) {
        await supabase
          .from('chats')
          .update({
            lastMessage: lastMessage.text,
            lastMessageTime: lastMessage.created_at
          })
          .eq('id', chatId);
      } else {
        // No messages left
        await supabase
          .from('chats')
          .update({
            lastMessage: null,
            lastMessageTime: null
          })
          .eq('id', chatId);
      }

      return true;
    } catch (error) {
      console.error('Error deleting message:', error);
      return false;
    }
  },

  addReaction: async (messageId: string, userId: string, emoji: string): Promise<boolean> => {
    if (!messageId || !userId || !emoji) return false;

    try {
      // Upsert the reaction (insert if not exists, update if exists)
      const { error } = await supabase
        .from('message_reactions')
        .upsert(
          { message_id: messageId, user_id: userId, emoji, created_at: new Date().toISOString() },
          { onConflict: 'message_id,user_id' }
        );

      if (error) throw error;

      return true;
    } catch (error) {
      console.error('Error adding reaction:', error);
      return false;
    }
  },

  removeReaction: async (messageId: string, userId: string): Promise<boolean> => {
    if (!messageId || !userId) return false;

    try {
      const { error } = await supabase
        .from('message_reactions')
        .delete()
        .eq('message_id', messageId)
        .eq('user_id', userId);

      if (error) throw error;

      return true;
    } catch (error) {
      console.error('Error removing reaction:', error);
      return false;
    }
  },

  // Link preview
  fetchLinkPreview: async (url: string): Promise<LinkPreview | null> => {
    try {
      const secureUrl = ensureHttps(url);
      const response = await fetch(`/api/sites?url=${encodeURIComponent(secureUrl)}`);

      if (!response.ok) {
        throw new Error('Failed to fetch link preview');
      }

      const data = await response.json();

      return {
        title: data.title || 'No title available',
        description: data.description || 'No description available',
        image: data.image || '',
        url: secureUrl,
      };
    } catch (error) {
      console.error('Error fetching link preview:', error);
      return null;
    }
  },

  // Realtime subscriptions
  subscribeToChats: (userId: string, callback: (payload: any) => void) => {
    return supabase
      .channel(`user-chats-${userId}`)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'chats',
        filter: `id=in.(select "chatId" from chat_participants where "userId"='${userId}')`,
      }, callback)
      .subscribe();
  },

  subscribeToMessages: (chatId: string, callback: (payload: any) => void) => {
    return supabase
      .channel(`chat-messages-${chatId}`)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'messages',
        // Use correct column name to ensure realtime updates fire
        filter: `chat_id=eq.${chatId}`,
      }, callback)
      .subscribe();
  },

  subscribeToReactions: (messageIds: string[], callback: (payload: any) => void) => {
    if (!messageIds.length) return null;

    return supabase
      .channel(`message-reactions-${messageIds.join('-')}`)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'message_reactions',
        // Fix column name to match database schema
        filter: `message_id=in.(${messageIds.map(id => `'${id}'`).join(',')})`,

      }, callback)
      .subscribe();
  },

  subscribeToUserPresence: (userIds: string[], callback: (payload: any) => void) => {
    if (!userIds.length) return null;

    return supabase
      .channel(`user-presence-${userIds.join('-')}`)
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'profiles',
        filter: `userId=in.(${userIds.map(id => `'${id}'`).join(',')})`,
      }, callback)
      .subscribe();
  }
};

// Helper function to create a stored procedure for finding direct chats
export const createFindDirectChatFunction = async () => {
  try {
    // SQL to create the function that finds direct chats between two users
    const { error } = await supabase.rpc('create_find_direct_chat_function', {});

    if (error) {
      // If the RPC doesn't exist, create it directly with raw SQL
      const { error: sqlError } = await supabase.functions.invoke('execute-sql', {
        body: {
          sql: `
            CREATE OR REPLACE FUNCTION find_direct_chat(user_id_1 TEXT, user_id_2 TEXT)
            RETURNS TABLE(chat_id TEXT) AS $$
            BEGIN
              RETURN QUERY
              SELECT c.id
              FROM chats c
              JOIN chat_participants p1 ON c.id = p1.chat_id AND p1.user_id = user_id_1
              JOIN chat_participants p2 ON c.id = p2.chat_id AND p2.user_id = user_id_2
              WHERE c.type = 'direct'
              AND (
                SELECT COUNT(*) FROM chat_participants WHERE chat_id = c.id
              ) = 2;
            END;
            $$ LANGUAGE plpgsql;
          `
        }
      });

      if (sqlError) {
        console.error('Error creating find_direct_chat function with raw SQL:', sqlError);
      } else {
        console.log('Created find_direct_chat function with raw SQL');
      }
    } else {
      console.log('Created find_direct_chat function with RPC');
    }
  } catch (error) {
    console.error('Error creating find_direct_chat function:', error);
  }
};

// Call this once when the app initializes
const initializeMessagingService = async () => {
  // Create the find_direct_chat function if it doesn't exist
  await createFindDirectChatFunction();
};
